import Auth0JS from 'auth0-js';
import Cookie from 'js-cookie';
import _ from 'lodash';
import { logger } from '@ubeeqo/web-utils';
import currentDomain from '@/utils/GetDomain';
import islocalhost from '@/utils/IsLocalhost';
import Setup from '@/utils/Setup';

let tokenRenewalTimeout;
const customLogin = Cookie.get('customLogin') ? '/signin' : '/';

export default class AuthService {
  authenticated = this.isAuthenticated();

  storedAuthData = Cookie.get('authData');

  tokenRenewalTimeout;

  expiresAt;

  accessToken;

  idToken;

  constructor(config) {
    this.login = this.login.bind(this);
    this.setSession = this.setSession.bind(this);
    this.logout = this.logout.bind(this);
    this.renewAuth = this.renewAuth.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.router = config.router;
    this.store = config.store;
    this.ENV = config.ENV;
    this.config = _.omit(config, ['router']);

    this.auth0 = new Auth0JS.WebAuth({
      ..._.omit(config, ['router']),
      responseType: 'token id_token',
      scope: 'openid',
    });
  }

  login() {
    if (customLogin === '/') {
      this.auth0.authorize();
    } else {
      this.router.replace(customLogin);
    }
  }

  renewAuth() {
    this.auth0.renewAuth({
      usePostMessage: true,
      redirectUri: `${window.location.origin}/authenticating`,
    }, () => {});
  }

  handleAuthentication() {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash((err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.setSession(authResult, (data) => {
            this.router.replace('/');
            resolve(data);
          });
        } else if (err) {
          reject(err);
        } else {
          resolve({
            authenticated: this.authenticated,
          });
        }
      });
    });
  }

  setSession(authResult, callback) {
    // Set the time that the Access Token will expire at
    this.expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
    const authData = authResult
      ? {
        accessToken: authResult.accessToken,
        idToken: authResult.idToken,
        expiresAt: this.expiresAt,
        idp_uuid: authResult.idTokenPayload.sub,
      }
      : this.storedAuthData;

    // update AxiosClient instance with new token.
    Setup.initAxiosClientConfig(this.ENV, JSON.stringify(authData));

    Cookie.set('authData', authData, { domain: currentDomain, ...!islocalhost && { secure: true } });
    callback({
      authenticated: true,
    });
  }

  logout() {
    Cookie.set('isLoggedIn', false);
    Cookie.remove('authData', { domain: currentDomain });
    this.userProfile = null;
    clearTimeout(tokenRenewalTimeout);
    this.login();
  }

  isAuthenticated() {
    const expiresAtFromLocalStorage = Cookie.get('authData') || localStorage.getItem('authData');
    const authData = expiresAtFromLocalStorage && JSON.parse(expiresAtFromLocalStorage);
    const now = new Date().getTime();
    const { expiresAt } = authData || this.storedAuthData || {};
    const isAuthenticated = now < expiresAt;

    return authData ? isAuthenticated : false;
  }

  renewTokens() {
    this.auth0.checkSession({}, (err, result) => {
      if (err) {
        logger.error(err);
      } else {
        this.localLogin(result);
      }
    });
  }

  scheduleRenewal() {
    const delay = this.expiresAt - Date.now();
    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewTokens();
      }, delay);
    }
  }

  localLogin(authResult) {
    // Set isLoggedIn flag in localStorage
    Cookie.set('isLoggedIn', true);
    // Set the time that the access token will expire at
    this.expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
    this.accessToken = authResult.accessToken;
    this.idToken = authResult.idToken;
    this.scheduleRenewal();
  }

  resetSession() {
    this.auth0.checkSession({}, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult, () => {
          this.router.push('/');
        });
        this.scheduleRenewal();
      } else if (err) {
        this.logout();
      }
    });
  }
}
